import { OverlayModule } from '@angular/cdk/overlay'
import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  OnInit,
  Output,
  ViewEncapsulation,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild,
  viewChildren,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { ActivatedRoute, Router } from '@angular/router'

import { Store } from '@ngrx/store'
import { MenuItem } from 'primeng/api'
import { DividerModule } from 'primeng/divider'
import { Menu } from 'primeng/menu'
import { TooltipModule } from 'primeng/tooltip'

import { EMPTY, Observable, from, of } from 'rxjs'
import { catchError, filter, map, switchMap, tap } from 'rxjs/operators'

import {
  FlxButtonModule,
  FlxIconModule,
  FlxLabelComponent,
  FlxModalModule,
  FlxModalRef,
  FlxModalService,
  FlxSwitchComponent,
} from '@flowx/angular-ui-toolkit'
import { BuildAction, BuildObject } from '@flowx/designer/modules/analytics/analytics-build.entity'
import { AnalyticsService } from '@flowx/designer/modules/analytics/analytics.service'
import { FlxMergeConflictsModalComponent } from '@flowx/designer/modules/merge-conflicts/components/merge-conflicts-modal/merge-conflicts-modal.component'
import {
  ComparedResource,
  ConflictsStore,
  MergeConflicts,
  MergeStatus,
  MergeVersionPayload,
  SameNameIdentifiers,
} from '@flowx/designer/modules/merge-conflicts/merge-conflicts.dictionary'
import { MergeConflictsStore } from '@flowx/designer/modules/merge-conflicts/stores/conflicts.store'
import { AppVersionsStore } from '@flowx/designer/modules/process-details/components/branching-overlay/application-versions.store'
import { NewBranchModalComponent } from '@flowx/designer/modules/process-details/components/new-branch-modal/new-branch-modal.component'
import { VersionActionsComponent } from '@flowx/designer/modules/process-details/components/process-version-actions/version-actions.component'
import {
  SubmitChangesModalComponent,
  SubmitChangesModalResult,
} from '@flowx/designer/modules/process-details/components/submit-changes-modal/submit-changes-modal.component'
import { FlxTagBuildModalComponent } from '@flowx/designer/modules/process-details/components/tag-build-modal/tag-build-modal.component'
import { FlxVersionChangesComponent } from '@flowx/designer/modules/process-details/components/version-changes/version-changes.component'
import { FlxBuildTagComponent } from '@flowx/designer/modules/projects/components/build-tag/build-tag.component'
import { ImportProjectStore } from '@flowx/designer/modules/projects/components/projects/import-project.store'
import { DefaultMenuItemStyle } from '@flowx/designer/modules/projects/components/resources-page/menu-items.constants'
import { ApplicationDetailsStore } from '@flowx/designer/modules/projects/store/application-version-details.store'
import { VersionChangesStore } from '@flowx/designer/modules/version-changes/version-changes.store'
import { serializeComparedResource } from '@flowx/designer/modules/version-changes/version-changes.utils'
import { FlxVersionCompareModalComponent } from '@flowx/designer/modules/version-changes/version-compare-modal/version-compare-modal.component'
import { DetailedPermissions } from '@flowx/designer/modules/workspaces/shared/workspace.utils'
import { WorkspacesAndPermissionsStore } from '@flowx/designer/modules/workspaces/workspaces-and-permissions.store'
import { FlxDateFormatPipe } from '@flowx/designer/pipes/flx-date.pipe'
import { FlxUserInitialsPipe } from '@flowx/designer/pipes/user-initials.pipe'
import { ConfirmationModalComponent } from '@flowx/designer/shared/components/confirmation-modal/confirmation-modal.component'
import {
  readStoredRecord,
  writeStoredRecord,
} from '@flowx/designer/shared/components/floating-panel/floating-panel-storage'
import { GitgraphComponent } from '@flowx/designer/shared/components/gitgraph/gitgraph.component'
import { IdeaHelperBoxComponent } from '@flowx/designer/shared/components/idea-helper-box/idea-helper-box.component'
import {
  ApplicationStatus,
  ApplicationVersion,
  BranchDeleteState,
  BuildTagPayload,
  MergedVersionState,
  VersionResourceChangeStatus,
} from '@flowx/designer/shared/entities/application.entity'
import { GuardCoordinatorService } from '@flowx/designer/shared/services/guard-coordinator.service'
import { RoutePersistenceService } from '@flowx/designer/shared/services/route-persistence.service'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { FlxBranchInfoComponent } from '@flowx/designer/shared/versioning/branch-info/branch-info.component'
import { BranchMergeStateComponent } from '@flowx/designer/shared/versioning/branch-merge-state/branch-merge-state.component'
import { BranchNameComponent } from '@flowx/designer/shared/versioning/branch-name/branch-name.component'
import { FlxBranchStateComponent } from '@flowx/designer/shared/versioning/branch-state/branch-state.component'
import {
  ArchiveBranchAction,
  CommitChangesAction,
  CompareVersionsAction,
  ContinueMergeAction,
  CreateBuildAction,
  CreateNewBranchAction,
  DiscardChangesAction,
  DiscardMergeAction,
  EditCommitMessageAction,
  ExportVersionAction,
  MergeAction,
  StartNewVersionAction,
  UnarchiveBranchAction,
  VersionActionEvent,
  VersionActionType,
  VersionActionsMap,
  ViewCommitDetailsAction,
} from '@flowx/designer/shared/versioning/version-actions.constants'
import { FlxVersionInfoComponent } from '@flowx/designer/shared/versioning/version-info/version-info.component'
import {
  getVersionRowMessage,
  resolveBlockReasonsLabel,
} from '@flowx/designer/shared/versioning/versioning.utils'
import { loadLocalFile } from '@flowx/shared/utils'

import { FlxCompareVersioningInfoComponent } from '../../../../shared/components/resource-history/compare-versioning-info/compare-versioning-info.component'
import {
  selectQueryParam,
  selectWorkspaceId,
} from '../../../../shared/store/selectors/router.selectors'
import { MergeBranchModalComponent } from '../merge-branch-modal/merge-branch-modal.component'

// The actions that cost the user their uncommitted work. They either destroy the selected version
// (commit, delete draft, discard merge, archive branch) or redirect off it (start new version,
// create branch, merge), and each one mutates before it navigates — so applicationSettingsCanDeactivate
// can only ever prompt after the work is already gone, and for the two that never navigate it cannot
// prompt at all. Continue Merge is gated as the last cancellable point: resolving conflicts
// inevitably ends in a merge and a redirect, and prompting at completion would come after the merge
// landed.
const GATED_VERSION_ACTIONS = new Set<VersionActionType>([
  VersionActionType.ContinueMerge,
  VersionActionType.DiscardMerge,
  VersionActionType.CommitChanges,
  VersionActionType.Merge,
  VersionActionType.StartNewVersion,
  VersionActionType.CreateNewBranch,
  VersionActionType.DiscardChanges,
  VersionActionType.ArchiveBranch,
])

// One browser-wide entry for the tree's display preferences, shared by every user and project
const VERSIONING_TREE_STORAGE_KEY = 'versioning-tree'

@Component({
  selector: 'flx-branching-overlay',
  imports: [
    OverlayModule,
    FlxButtonModule,
    CommonModule,
    GitgraphComponent,
    DividerModule,
    TooltipModule,
    FlxIconModule,
    VersionActionsComponent,
    FlxUserInitialsPipe,
    FlxDateFormatPipe,
    BranchNameComponent,
    FlxBranchInfoComponent,
    FlxVersionInfoComponent,
    FlxBuildTagComponent,
    FlxBranchStateComponent,
    FlxMergeConflictsModalComponent,
    FlxModalModule,
    FlxVersionChangesComponent,
    FlxVersionCompareModalComponent,
    FlxCompareVersioningInfoComponent,
    FlxLabelComponent,
    FlxSwitchComponent,
    FormsModule,
    Menu,
    BranchMergeStateComponent,
    IdeaHelperBoxComponent,
  ],
  templateUrl: './branching-overlay.component.html',
  styleUrls: ['../../../../styles/versioning.scss'],
  host: { class: 'branching-overlay' },
  providers: [ImportProjectStore, VersionChangesStore],
  changeDetection: ChangeDetectionStrategy.Eager,
  encapsulation: ViewEncapsulation.None,
})
export class BranchingOverlayComponent implements OnInit {
  #router = inject(Router)
  #route = inject(ActivatedRoute)
  #appVersionsStore = inject(AppVersionsStore)
  #appDetailsStore = inject(ApplicationDetailsStore)
  #toastMessageService = inject(ToastMessageService)
  #flxModalService = inject(FlxModalService)
  #importProjectStore = inject(ImportProjectStore)
  #destroyRef = inject(DestroyRef)
  #mergeConflictsStore = inject(MergeConflictsStore)
  #versionChangesStore = inject(VersionChangesStore)
  #workspacesAndPermissionsStore = inject(WorkspacesAndPermissionsStore)
  #routePersistenceService = inject(RoutePersistenceService)
  #store = inject(Store)
  #analyticsService = inject(AnalyticsService)
  #elementRef = inject(ElementRef)
  #guardCoordinator = inject(GuardCoordinatorService)

  // Derived predicates shared by the versionActions entries — kept separate from config
  // assembly so every entry reaches for the same archived-aware flags (e.g. canEdit)
  #versionFlags = computed(() => {
    const version = this.applicationVersion()
    const isCompareMode = !!this.compareAnyTwoActive()
    const branchHeadStatus = this.selectedTargetBranchStats()?.headVersion?.status

    const isArchived = !!version?.branchArchived
    const canEdit = !!this.applicationAccessRights()?.canEdit && !isArchived
    const isCommitted = version?.status === ApplicationStatus.COMMITTED
    const showStandardActions = !!version?.hasContent && !isCompareMode

    return {
      isArchived,
      canEdit,
      isWip: version?.status === ApplicationStatus.WIP,
      isCommitted,
      // Continue/discard act on the selected version itself when it is the pending merge
      isMergeInProgress: version?.status === ApplicationStatus.MERGE_IN_PROGRESS && !isArchived,
      canStartNewVersion: canEdit && isCommitted,
      showStandardActions,
      canExport: showStandardActions && isCommitted && !isArchived,
      // Merge sources from the branch head, so only a WIP or merging head blocks it —
      // an older selected version stays mergeable
      canMergeBranch:
        branchHeadStatus !== ApplicationStatus.WIP &&
        branchHeadStatus !== ApplicationStatus.MERGE_IN_PROGRESS,
      isBranchHeadMerging: branchHeadStatus === ApplicationStatus.MERGE_IN_PROGRESS,
    }
  })

  private readonly branchInfo = viewChildren('branchInfo', { read: ElementRef })
  private readonly tableRows = viewChildren('tableRow', { read: ElementRef })
  private readonly rowCommitMessageElements = viewChildren('rowCommitMessageElement', {
    read: ElementRef,
  })
  private readonly gitLogScrollElement = viewChild('gitLogScrollElement', {
    read: ElementRef,
  })

  @Output() closeOverlay = new EventEmitter<void>()
  protected showConflictsModal = computed(() => {
    const activeMergeConflicts = this.#mergeConflictsStore.activeMergeConflicts()
    const mergeInProgress = this.#appVersionsStore.mergeInProgress()
    return activeMergeConflicts || mergeInProgress
  })
  appName = computed(() => this.applicationVersion().appName || '')
  applicationVersionId = input.required<ApplicationVersion['id']>()
  applicationId = input.required<ApplicationVersion['appId']>()
  workspaceId = this.#store.selectSignal(selectWorkspaceId)()
  branchId = input.required<ApplicationVersion['branchId']>()
  createVersionInProgress = this.#appVersionsStore.createVersionInProgress
  commitInProgress = this.#appVersionsStore.commitInProgress
  ApplicationStatus = ApplicationStatus
  applicationVersion = this.#appVersionsStore.selectedVersion
  history = this.#appVersionsStore.applicationVersions
  loading = this.#appVersionsStore.loading
  copiedTextToClipboard = false
  importInProgress = this.#importProjectStore.importInProgress
  casUserId = this.#workspacesAndPermissionsStore.casUserId
  allAvailableMergeToBranches = this.#appVersionsStore.mergeToBranches

  // Compare
  versionResources = this.#versionChangesStore.versionChanges
  compareWithVersion = computed(() => {
    const compareWithVersionIdQueryParam = this.compareWithVersionIdQueryParam()
    const currentVersion = this.applicationVersion()
    const allApplicationVersions = untracked(() => this.#appVersionsStore.applicationVersions())
    const compareWithVersionId = compareWithVersionIdQueryParam || currentVersion?.previousVersionId
    return allApplicationVersions.find((version) => version.id === compareWithVersionId)
  })
  noDifferencesMessage = computed(() => {
    const versionResources = this.versionResources.value()
    const currentVersion = this.applicationVersion()

    if (!currentVersion.hasContent) {
      return `Previous version is not available on this environment.`
    }
    if (currentVersion.branchArchived) {
      return `This branch has been archived.`
    }
    if (currentVersion.status === ApplicationStatus.MERGE_IN_PROGRESS) {
      return 'This version is currently being merged, please check back later for differences'
    }
    if (Object.keys(versionResources).length === 0) {
      return 'No differences'
    }
    return ''
  })
  comparedResource = this.#versionChangesStore.comparedResource
  isCompareOptionAvailable = computed(() => !!this.applicationVersion().previousVersionId)

  compareWithVersionIdQueryParam = this.#versionChangesStore.compareWithVersionId
  compareAnyTwoActive = computed(() => {
    const compareWithVersionId = this.compareWithVersionIdQueryParam()
    const compareWithVersion = this.compareWithVersion()
    return compareWithVersionId && compareWithVersion
  })

  // Permissions
  applicationAccessRights = input.required<DetailedPermissions>()
  buildsAccessRights = input.required<DetailedPermissions>()

  // Delete Branch guard rails — the branch-scoped derivations live in AppVersionsStore;
  // only the permission gating stays here (applicationAccessRights is a component input)
  selectedTargetBranchStats = this.#appVersionsStore.selectedTargetBranchStats
  deleteTargetChildBranchNames = this.#appVersionsStore.selectedTargetChildBranchNames
  selectedTargetBranchVersions = this.#appVersionsStore.selectedTargetBranchVersions

  deleteBranchState = computed<BranchDeleteState | null>(() => {
    const branchStats = this.selectedTargetBranchStats()
    const deleteTargetChildBranchNames = this.deleteTargetChildBranchNames()
    const selectedTargetBranchVersions = this.selectedTargetBranchVersions()
    if (!branchStats) {
      return null
    }

    const { headVersion, hasActiveMerge, unmergedCommitCount, buildCount } = branchStats
    const { canEdit, canDeleteBranch } = this.applicationAccessRights()
    const blockReason = resolveBlockReasonsLabel(
      {
        canNotEdit: !canEdit,
        canNotDelete: !canDeleteBranch,
        isMain: headVersion.branchName === 'main',
        isArchived: headVersion.branchArchived,
        hasChildBranches: !!deleteTargetChildBranchNames?.length,
        hasActiveMerge,
      },
      deleteTargetChildBranchNames
    )

    return {
      appVersion: headVersion,
      blockReason,
      childBranchNames: deleteTargetChildBranchNames,
      hasActiveMerge,
      unmergedCommitCount,
      buildCount,
      branchVersions: selectedTargetBranchVersions.length,
      hasWip: headVersion.status === ApplicationStatus.WIP,
    }
  })
  // End Delete Branch guard rails

  graphWidth = signal<number>(undefined)
  horizontalScrollableArea = computed(() => {
    const graphWidth = this.graphWidth()
    const branchInfo = this.branchInfo()
    // The message spans come and go with the Messages switch; reading them re-measures the rows after that render
    this.rowCommitMessageElements()
    return (
      graphWidth +
      branchInfo.reduce((acc, el) => {
        const elementWidth = el.nativeElement.offsetWidth
        if (acc < elementWidth) {
          return elementWidth
        }
        return acc
      }, 0)
    )
  })
  showBranchLabelsOutsideGitGraph = true

  // Archived branches toggle — URL-driven like compareWithVersionId, so the view survives reloads
  archivedQueryParam = this.#store.selectSignal(selectQueryParam('archived'))
  showArchived = computed(() => this.archivedQueryParam() === 'true')

  // Commit messages on the rows; on unless the user turned them off
  showCommitMessages = signal(
    readStoredRecord(VERSIONING_TREE_STORAGE_KEY).showCommitMessages !== false
  )
  rowCommitMessages = computed<Record<ApplicationVersion['id'], string | null>>(() =>
    Object.fromEntries(this.history().map((version) => [version.id, getVersionRowMessage(version)]))
  )

  // Details column state — compare and commit details are both URL-driven and mutually exclusive
  // (each navigation clears the other); compare wins should both params ever coexist. Commit details
  // also need a committed version: the param survives switching to a draft, which has no commit to show
  commitDetailsQueryParam = this.#store.selectSignal(selectQueryParam('commitDetails'))
  detailsView = computed<'compare' | 'commitDetails' | 'default'>(() =>
    this.compareWithVersionIdQueryParam()
      ? 'compare'
      : this.commitDetailsQueryParam() === 'true' && !!this.applicationVersion()?.committedBy
        ? 'commitDetails'
        : 'default'
  )
  editCommitMessageAction = computed(
    () => this.versionActions()[VersionActionType.EditCommitMessage]
  )

  branchContextTargetAppVersion = this.#appVersionsStore.branchContextTargetAppVersion

  closeBranchingState = computed(() => {
    const applicationVersion = this.applicationVersion()
    return {
      disabled: !applicationVersion.hasContent || applicationVersion.branchArchived,
      message: !applicationVersion.hasContent
        ? 'Previous version is not available on this environment.'
        : applicationVersion.branchArchived
          ? 'This branch has been archived.'
          : '',
    }
  })

  // Enum-keyed action configs consumed by version-actions / branch-info / version-info; clicks
  // come back as VersionActionEvent and are routed through handleVersionAction
  versionActions = computed<VersionActionsMap>(() => {
    const f = this.#versionFlags()
    const version = this.applicationVersion()
    const commitInProgress = this.commitInProgress()
    const createVersionInProgress = this.createVersionInProgress()
    const deleteBranchState = this.deleteBranchState()

    return {
      [VersionActionType.ContinueMerge]: ContinueMergeAction({
        visible: f.isMergeInProgress,
        enabled: true,
      }),
      [VersionActionType.DiscardMerge]: DiscardMergeAction({
        visible: f.isMergeInProgress,
        enabled: true,
      }),
      [VersionActionType.CommitChanges]: CommitChangesAction({
        visible: f.showStandardActions && f.canEdit && f.isWip,
        enabled: !commitInProgress,
      }),
      [VersionActionType.CreateBuild]: CreateBuildAction({
        visible: f.showStandardActions && f.canEdit && f.isCommitted,
        enabled: !version?.buildTag,
      }),
      [VersionActionType.Merge]: MergeAction({
        visible: f.showStandardActions && f.canEdit && (f.isCommitted || f.isMergeInProgress),
        enabled: f.canMergeBranch,
        disabledReason: f.isBranchHeadMerging
          ? 'A merge is already in progress on this branch'
          : undefined,
      }),
      [VersionActionType.StartNewVersion]: StartNewVersionAction({
        visible: f.showStandardActions && f.canStartNewVersion,
        enabled: !createVersionInProgress && !!version?.lastVersionInBranch,
        disabledReason: createVersionInProgress
          ? 'New version creation is in progress'
          : 'A new version can only be started from the latest version in the branch',
      }),
      [VersionActionType.CreateNewBranch]: CreateNewBranchAction({
        visible: f.showStandardActions && f.isCommitted && f.canEdit,
        enabled: true,
      }),
      [VersionActionType.ExportVersion]: ExportVersionAction({
        visible: f.canExport,
        enabled: true,
      }),
      [VersionActionType.DiscardChanges]: DiscardChangesAction({
        visible: f.showStandardActions && f.canEdit && f.isWip,
        enabled: !!version?.previousVersionId,
      }),
      [VersionActionType.ArchiveBranch]: ArchiveBranchAction({
        visible: !!deleteBranchState && !deleteBranchState.appVersion.branchArchived,
        enabled: !deleteBranchState?.blockReason,
        blockReason: deleteBranchState?.blockReason,
      }),
      [VersionActionType.UnarchiveBranch]: UnarchiveBranchAction({
        visible: f.isArchived,
        enabled: true,
      }),
      [VersionActionType.EditCommitMessage]: EditCommitMessageAction({
        visible: f.canEdit && f.isCommitted && version?.hasContent === true,
        enabled: true,
      }),
      [VersionActionType.CompareVersions]: CompareVersionsAction({
        visible: f.showStandardActions && !!version?.previousVersionId,
        enabled: true,
      }),
      [VersionActionType.ViewCommitDetails]: ViewCommitDetailsAction({
        visible: !!version?.committedBy,
        enabled: true,
      }),
    }
  })

  allBranchesContextMenuItems = signal<MenuItem[]>([
    {
      label: 'Import Version',
      icon: 'ph ph-upload-simple',
      command: () => this.importProcess(),
      ...DefaultMenuItemStyle,
    },
  ])

  mergedVersionState = computed<MergedVersionState | null>(() => {
    const applicationVersion = this.applicationVersion()
    const history = this.history()
    if (!applicationVersion.mergedFromVersionId) {
      return null
    }
    const mergedVersion = history.find(
      (version) => version.id === applicationVersion.mergedFromVersionId
    )
    return {
      mergedFromBranchName: mergedVersion?.branchName ?? applicationVersion.mergedFromBranchName,
      mergeInProgress: applicationVersion.status === ApplicationStatus.MERGE_IN_PROGRESS,
      isMergedBranchArchived: !mergedVersion || mergedVersion?.branchArchived,
    }
  })

  constructor() {
    effect(() => {
      const applicationId = this.applicationId()
      const branchId = this.branchId()
      this.getAllAvailableMergeToBranches(applicationId, branchId)
    })

    // Re-center the selected version whenever the graph view changes: the selection
    // (navigateToVersion), the graph geometry (the SVG rebuild/resize re-emits the width), or
    // the rows re-rendering (history reload). setTimeout defers past the CD pass that applies
    // min-width and the lib's async SVG sizing.
    effect(() => {
      const selectedVersionId = this.applicationVersion()?.id
      const horizontalScrollableArea = this.horizontalScrollableArea()
      this.tableRows()
      if (!selectedVersionId || !horizontalScrollableArea) {
        return
      }
      setTimeout(() => this.scrollVersionIntoView(selectedVersionId))
    })
  }

  ngOnInit(): void {
    this.getApplicationHistory()
  }

  navigateToVersion(nextApplicationVersionId: ApplicationVersion['id']): void {
    // Archived versions are never navigated to (the route must not point at them), and the
    // version already in the URL needs no navigation either — Router.navigate resolves null
    // for same-URL navigations, which the success check below would treat as a failure and
    // skip the selection. Both cases are overlay-local re-selects.
    const nextVersion = this.history().find((version) => version.id === nextApplicationVersionId)
    if (
      nextVersion?.branchArchived ||
      nextApplicationVersionId === this.applicationVersionId() ||
      nextVersion?.status === ApplicationStatus.MERGE_IN_PROGRESS
    ) {
      this.#appVersionsStore.setSelectVersionId(nextApplicationVersionId)
      this.#router.navigate([], {
        relativeTo: this.#route,
        queryParams: { commitDetails: false },
        queryParamsHandling: 'merge',
      })
      return
    }

    this.#routePersistenceService
      .getUpdatedRoute(nextApplicationVersionId)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((routingData) => {
        const { fragments, queryParams } = routingData
        this.#router
          .navigate(['/', ...fragments], {
            relativeTo: this.#route,
            queryParams: { ...queryParams, graphOpen: true, commitDetails: false },
            queryParamsHandling: 'merge',
          })
          .then((navigationSucceeded) => {
            if (navigationSucceeded) {
              this.#appVersionsStore.setSelectVersionId(nextApplicationVersionId)
            }
          })
      })
  }

  closeBranchOverlayModal(): void {
    this.closeOverlay.emit()
  }

  handleVersionAction(event: VersionActionEvent): void {
    if (!GATED_VERSION_ACTIONS.has(event.action)) {
      this.#dispatchVersionAction(event)
      return
    }

    this.#guardCoordinator
      .confirmProceed()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((canProceed) => {
        if (canProceed) {
          this.#dispatchVersionAction(event)
        }
      })
  }

  openSubmitMessageModal(): void {
    const modalRef = this.#flxModalService.show(
      SubmitChangesModalComponent,
      {
        closeOnBackdropClick: true,
        backdropClass: 'overlay-backdrop',
        closeOnEscapeKey: true,
      },
      {
        mode: 'commit',
        subtitle: 'The draft changes become a committed version on this branch',
        applicationVersion: this.applicationVersion(),
      }
    )

    modalRef.data$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ submitMessage, description }: SubmitChangesModalResult) => {
        this.commitChanges(submitMessage, description)
      })
  }

  async commitChanges(commitMessage: string, description?: string): Promise<void> {
    try {
      const committedVersion = await this.#appVersionsStore.commitChanges(
        this.applicationVersion().appId,
        this.applicationVersion().id,
        { commitMessage, commitDescription: description }
      )
      this.#toastMessageService.showSuccessMessage('Changes committed successfully')
      this.#appDetailsStore.updateApplication(committedVersion)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(error.error?.title || 'Failed to commit changes')
    }
  }

  createNewBranch(): void {
    this.#flxModalService
      .show(
        NewBranchModalComponent,
        {
          closeOnBackdropClick: true,
          backdropClass: 'overlay-backdrop',
        },
        {
          fromBranchName: this.applicationVersion().branchName,
        }
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((branchName) => {
        this.startWorkInProgress(branchName)
      })
  }

  async startWorkInProgress(branchName?: ApplicationVersion['branchName']): Promise<void> {
    try {
      const newVersion = await this.#appVersionsStore.createNewVersion(
        this.applicationVersion().appId,
        this.applicationVersion().id,
        {
          branchName: branchName || this.applicationVersion().branchName,
        }
      )
      this.#toastMessageService.showSuccessMessage(
        branchName ? 'New branch created successfully' : 'New version created successfully'
      )
      this.navigateToVersion(newVersion.id)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title || branchName
          ? 'Failed to create new branch'
          : 'Failed to create new version'
      )
    }
  }

  mergeWithBranch(): void {
    this.#flxModalService
      .show(
        MergeBranchModalComponent,
        {
          closeOnBackdropClick: true,
          backdropClass: 'overlay-backdrop',
          closeOnEscapeKey: true,
        },
        {
          applicationVersionName: this.applicationVersion().branchName,
          branches: this.#appVersionsStore.mergeToBranches(),
        }
      )
      .data$.pipe(
        map((mergePayload: MergeVersionPayload) => ({
          mergePayload,
          mergeSourceVersion:
            this.selectedTargetBranchVersions().find(
              (version) =>
                version.lastVersionInBranch && version.status === ApplicationStatus.COMMITTED
            ) ?? this.applicationVersion(),
        })),
        switchMap(({ mergePayload, mergeSourceVersion }) =>
          this.handleMergePayload(mergePayload, mergeSourceVersion)
        ),
        filter(Boolean),
        switchMap(({ mergePayload, sourceVersion }) =>
          this.performMerge(mergePayload, sourceVersion)
        ),
        catchError((error) => this.handleMergeError(error)),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  async checkForResourcesWithSameName(
    mergedVersion: MergeVersionPayload,
    sourceVersion: ApplicationVersion
  ): Promise<SameNameIdentifiers> {
    return await this.#mergeConflictsStore.checkForDuplicateIdentifiers(
      sourceVersion.appId,
      sourceVersion.id,
      mergedVersion
    )
  }

  async merge(
    mergedVersion: MergeVersionPayload,
    sourceVersion: ApplicationVersion
  ): Promise<Partial<MergeConflicts>> {
    const mergeConflictsResponse = await this.#appVersionsStore.mergeWithBranch(
      sourceVersion.appId,
      sourceVersion.id,
      mergedVersion
    )

    this.#mergeConflictsStore.setMergeVersionPayload(mergedVersion)
    return mergeConflictsResponse
  }

  async mergeResume(mergeId: ApplicationVersion['mergeId']): Promise<void> {
    const { appId, id, status, mergedFromVersionId } = this.applicationVersion()
    const appVersionId =
      status === ApplicationStatus.MERGE_IN_PROGRESS ? (mergedFromVersionId ?? id) : id
    const mergeStatus = await this.#mergeConflictsStore.mergeResume(
      appId,
      appVersionId,
      mergeId,
      false
    )
    if (mergeStatus === MergeStatus.MergeConflicts || mergeStatus === MergeStatus.MergeInProgress) {
      this.#router.navigate([{ outlets: { mergeConflicts: mergeId } }], {
        relativeTo: this.#route.firstChild,
        queryParamsHandling: 'preserve',
      })
    }
    if (mergeStatus === MergeStatus.MergeFailed) {
      this.#router.navigate([{ outlets: { mergeConflicts: [mergeId, 'errors'] } }], {
        relativeTo: this.#route.firstChild,
        queryParamsHandling: 'preserve',
      })
    }
  }

  mergeDiscard(mergeId: ApplicationVersion['mergeId']): void {
    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
        {
          title: 'Are you sure you want to discard current Merge?',
          btnOkText: 'Proceed',
        }
      )
      .data$.pipe(
        switchMap(({ confirm }) => {
          if (confirm) {
            this.onDiscardManualMerge(mergeId)
          }
          return of(null)
        }),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  onEditSubmitMessage(): void {
    this.#flxModalService
      .show(
        SubmitChangesModalComponent,
        {
          closeOnBackdropClick: true,
          backdropClass: 'overlay-backdrop',
          closeOnEscapeKey: true,
        },
        {
          submitMessage: this.applicationVersion().commitMessage,
          description: this.applicationVersion().commitDescription,
          title: 'Edit Commit Message',
          mode: 'rename',
          applicationVersion: this.applicationVersion(),
        }
      )
      .data$.pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(({ submitMessage, description }: SubmitChangesModalResult) => {
        this.renameCommitMessage(submitMessage, description)
      })
  }

  async renameCommitMessage(commitMessage: string, description?: string): Promise<void> {
    try {
      const updatedVersion = await this.#appVersionsStore.renameCommitMessage(
        this.applicationVersion().appId,
        this.applicationVersion().id,
        {
          commitMessage,
          commitDescription: description,
          commitDescriptionBody: this.applicationVersion().commitDescriptionBody,
        }
      )
      this.#toastMessageService.showSuccessMessage('Commit details updated successfully.')
      this.#appDetailsStore.updateApplication(updatedVersion)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title || 'Failed to rename commit message'
      )
    }
  }

  startDiscardChanges(): void {
    this.#flxModalService
      .show(
        ConfirmationModalComponent,
        { closeOnBackdropClick: false, backdropClass: 'overlay-backdrop' },
        {
          title: 'Delete Draft Version',
          message:
            'All uncommitted changes in this draft will be permanently deleted. The branch will remain unchanged at its last committed version.',
          btnOkText: 'Delete Draft',
        }
      )
      .data$.pipe(
        switchMap(({ confirm }) => {
          if (confirm) {
            this.discardChanges()
          }
          return of(null)
        })
      )
      .subscribe()
  }

  discardChanges(): void {
    try {
      this.#appVersionsStore.discardChanges(
        this.applicationVersion().appId,
        this.applicationVersion().id
      )
      this.#toastMessageService.showSuccessMessage('Changes successfully discarded')
      this.navigateToVersion(this.applicationVersion().previousVersionId)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(error.error?.title || 'Failed to discard changes')
    }
  }

  importProcess(): void {
    loadLocalFile({ accept: ['.zip'] })
      .pipe(
        switchMap((files) =>
          this.#importProjectStore.importApplication(this.casUserId(), files[0])
        ),
        takeUntilDestroyed(this.#destroyRef)
      )
      .subscribe()
  }

  onCopyToClipboardSuccess(): void {
    this.copiedTextToClipboard = true
    setTimeout(() => (this.copiedTextToClipboard = false), 2000)
  }

  getApplicationHistory(archived = this.showArchived()): void {
    try {
      this.#appVersionsStore.getHistory(this.applicationId(), this.applicationVersionId(), archived)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title || 'Failed to fetch application history'
      )
    }
  }

  toggleArchived(): void {
    // Passed through explicitly instead of re-read from the URL — the router-store selector
    // may not have settled yet when the navigation promise resolves
    const archived = !this.showArchived()
    this.#router
      .navigate([], {
        relativeTo: this.#route,
        queryParams: { archived: archived ? true : null },
        queryParamsHandling: 'merge',
      })
      .then(() => this.getApplicationHistory(archived))
  }

  toggleCommitMessages(showCommitMessages: boolean): void {
    this.showCommitMessages.set(showCommitMessages)
    writeStoredRecord(VERSIONING_TREE_STORAGE_KEY, { showCommitMessages })
  }

  getAllAvailableMergeToBranches(
    applicationId: ApplicationVersion['appId'],
    branchId: ApplicationVersion['branchId']
  ): void {
    try {
      this.#appVersionsStore.getAllAvailableMergeToBranches(applicationId, branchId)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(
        error.error?.title || 'Failed to fetch available merge branches'
      )
    }
  }

  addVersionTag(): void {
    const modal = this.#flxModalService.show(
      FlxTagBuildModalComponent,
      {
        closeOnBackdropClick: true,
        backdropClass: 'overlay-backdrop',
        closeOnEscapeKey: true,
      },
      { appName: this.applicationVersion().appName }
    )
    modal.data$
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap((buildTagPayload: BuildTagPayload) => {
          this.tagVersion(buildTagPayload, modal)
        })
      )
      .subscribe()
  }

  showCommitStatus(commitLog: ApplicationVersion): boolean {
    return (
      commitLog.id === this.applicationVersion()?.id || commitLog.status === ApplicationStatus.WIP
    )
  }

  async closeMergeConflictsModalAndRedirect(): Promise<boolean> {
    this.#mergeConflictsStore.clearStore()
    return this.#router.navigate([{ outlets: { mergeConflicts: null } }], {
      relativeTo: this.#route.firstChild,
      queryParamsHandling: 'preserve',
    })
  }

  async onManualMergeCompleted(newVersion: ApplicationVersion): Promise<void> {
    await this.closeMergeConflictsModalAndRedirect()
    if (newVersion) {
      this.#appVersionsStore.updateVersionById(newVersion.id, newVersion)
      if (newVersion.status === ApplicationStatus.COMMITTED) {
        this.navigateToVersion(newVersion.id)
      }
    }
  }

  async onDiscardManualMerge(mergeConflictId: ConflictsStore['mergeConflictId']): Promise<void> {
    if (this.showConflictsModal()) {
      this.closeMergeConflictsModalAndRedirect()
    }
    try {
      await this.#appVersionsStore.discardMerge(mergeConflictId)
      this.#toastMessageService.showSuccessMessage('Merge discarded successfully')
      this.getAllAvailableMergeToBranches(this.applicationId(), this.branchId())
    } catch (error) {
      this.#toastMessageService.showErrorMessage('Error discarding merge')
    }
  }

  toggleResourceCompare(
    event: (ComparedResource & { resourceChangeStatus: VersionResourceChangeStatus }) | null = null
  ): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        comparedResource: event ? serializeComparedResource(event) : null,
      },
      queryParamsHandling: 'merge',
    })
  }

  toggleCompareWithVersion(compareWithVersionId: string = null): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        compareWithVersionId,
        commitDetails: null,
      },
      queryParamsHandling: 'merge',
    })
  }

  toggleCommitDetails(show: boolean): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        commitDetails: show ? true : null,
        compareWithVersionId: null,
      },
      queryParamsHandling: 'merge',
    })
  }

  // Shared back button of the compare / commit-details sub-header
  exitDetailsView(): void {
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: { compareWithVersionId: null, commitDetails: null },
      queryParamsHandling: 'merge',
    })
  }

  async archiveBranch({
    appVersion,
    reason,
  }: {
    appVersion: ApplicationVersion
    reason: string
  }): Promise<void> {
    try {
      const parentBranchHeadId = await this.#appVersionsStore.archiveBranch(
        this.applicationId(),
        appVersion.branchId,
        reason
      )
      await this.#appVersionsStore.getHistory(appVersion.appId, appVersion.id, this.showArchived())
      this.#toastMessageService.showSuccessMessage('Branch archived successfully')
      this.navigateToVersion(parentBranchHeadId)
    } catch (error) {
      this.#toastMessageService.showErrorMessage(error.error?.title || 'Failed to delete branch')
    }
  }

  async unarchiveBranch(appVersion: ApplicationVersion): Promise<void> {
    try {
      await this.#appVersionsStore.unarchiveBranch(appVersion)
      await this.#appVersionsStore.getHistory(appVersion.appId, appVersion.id, this.showArchived())
      this.navigateToVersion(appVersion.id)
      this.#toastMessageService.showSuccessMessage('Branch unarchived successfully')
    } catch (error) {
      this.#toastMessageService.showErrorMessage(error.error?.title || 'Failed to unarchive branch')
    }
  }

  private scrollVersionIntoView(versionId: ApplicationVersion['id']): void {
    const scrollContainer: HTMLElement = this.gitLogScrollElement()?.nativeElement
    if (!scrollContainer) {
      return
    }

    const row = this.tableRows().find(
      (tableRow) => tableRow.nativeElement.id === versionId.toString()
    )
    if (row) {
      const rowRect = row.nativeElement.getBoundingClientRect()
      const containerTop = scrollContainer.getBoundingClientRect().top
      const containerBottom = containerTop + scrollContainer.clientHeight
      const rowInView = rowRect.top >= containerTop && rowRect.bottom <= containerBottom
      if (!rowInView) {
        const rowCenterWithinContainer = rowRect.top + rowRect.height / 2 - containerTop
        scrollContainer.scrollTop += rowCenterWithinContainer - scrollContainer.clientHeight / 2
      }
    }

    const targetDot: SVGGElement = this.#elementRef.nativeElement.querySelector(
      `[data-commit-id="${versionId}"]`
    )
    if (!targetDot) {
      return
    }

    const dotRect = targetDot.getBoundingClientRect()
    const containerRect = scrollContainer.getBoundingClientRect()
    const dotCenterWithinContainer = dotRect.left + dotRect.width / 2 - containerRect.left
    scrollContainer.scrollLeft += dotCenterWithinContainer - containerRect.width / 2
  }

  private async tagVersion(tagPayload: BuildTagPayload, modal: FlxModalRef): Promise<void> {
    try {
      await this.#appVersionsStore.tagBuild(
        this.applicationVersion().appId,
        this.applicationVersion().id,
        tagPayload
      )
      this.#analyticsService.trackBuild({
        event: { object: BuildObject.BUILD, action: BuildAction.CREATED },
        properties: { outcome: 'success' },
      })
      this.#toastMessageService.showSuccessMessage('Build successfully created')
      modal.close()
    } catch (error) {
      this.#analyticsService.trackBuild({
        event: { object: BuildObject.BUILD, action: BuildAction.CREATED },
        properties: { outcome: 'failed' },
      })
      this.#toastMessageService.showErrorMessage(error.error?.title || 'Failed to tag build')
    }
  }

  private handleMergeError(error: any): Observable<never> {
    this.#mergeConflictsStore.clearStore()
    this.#toastMessageService.showErrorMessage(error?.title || 'Failed to merge branch')
    return EMPTY
  }

  private handleMergePayload(
    mergePayload: MergeVersionPayload,
    sourceVersion: ApplicationVersion
  ): Observable<{ mergePayload: MergeVersionPayload; sourceVersion: ApplicationVersion } | null> {
    return from(this.checkForResourcesWithSameName(mergePayload, sourceVersion)).pipe(
      tap((sameNameResources) => this.handleSameNameResources(sameNameResources)),
      map((sameNameResources) =>
        sameNameResources.duplicates.length ? null : { mergePayload, sourceVersion }
      )
    )
  }

  private handleSameNameResources(sameNameResources: SameNameIdentifiers): void {
    if (sameNameResources.duplicates.length) {
      this.#router.navigate([{ outlets: { mergeConflicts: ['duplicate-name-entries'] } }], {
        relativeTo: this.#route.firstChild,
        queryParamsHandling: 'preserve',
      })
    }
  }

  private performMerge(
    mergePayload: MergeVersionPayload,
    sourceVersion: ApplicationVersion
  ): Observable<Partial<MergeConflicts>> {
    return from(this.merge(mergePayload, sourceVersion)).pipe(
      tap((mergeConflictsResult) => this.handleMergeResult(mergeConflictsResult))
    )
  }

  private handleMergeResult(mergeConflictsResult: Partial<MergeConflicts>): void {
    const { mergeStatus, mergeId, mergedAppVersion } = mergeConflictsResult
    if (mergeStatus === MergeStatus.MergeFailed) {
      this.#mergeConflictsStore.setErrorData(mergeConflictsResult)
      this.#router.navigate([{ outlets: { mergeConflicts: [mergeId, 'errors'] } }], {
        relativeTo: this.#route.firstChild,
        queryParamsHandling: 'preserve',
      })
      return
    }
    if (mergedAppVersion.status === ApplicationStatus.MERGE_IN_PROGRESS && mergeId) {
      this.#router.navigate([{ outlets: { mergeConflicts: mergeId } }], {
        relativeTo: this.#route.firstChild,
        queryParamsHandling: 'preserve',
      })
      return
    }
    this.#mergeConflictsStore.clearStore()
    this.#toastMessageService.showSuccessMessage('Branch merged successfully')
    this.navigateToVersion(mergedAppVersion.id)
  }

  #dispatchVersionAction(event: VersionActionEvent): void {
    switch (event.action) {
      case VersionActionType.ContinueMerge:
        this.mergeResume(this.applicationVersion()?.mergeId)
        break
      case VersionActionType.DiscardMerge:
        this.mergeDiscard(this.applicationVersion()?.mergeId)
        break
      case VersionActionType.CommitChanges:
        this.openSubmitMessageModal()
        break
      case VersionActionType.CreateBuild:
        this.addVersionTag()
        break
      case VersionActionType.Merge:
        this.mergeWithBranch()
        break
      case VersionActionType.StartNewVersion:
        this.startWorkInProgress()
        break
      case VersionActionType.CreateNewBranch:
        this.createNewBranch()
        break
      case VersionActionType.DiscardChanges:
        this.startDiscardChanges()
        break
      case VersionActionType.ArchiveBranch:
        this.archiveBranch(event.payload)
        break
      case VersionActionType.UnarchiveBranch:
        this.unarchiveBranch(event.payload)
        break
      case VersionActionType.EditCommitMessage:
        this.onEditSubmitMessage()
        break
      case VersionActionType.CompareVersions:
        this.toggleCompareWithVersion(this.applicationVersion().previousVersionId)
        break
      case VersionActionType.ViewCommitDetails:
        this.toggleCommitDetails(true)
        break
    }
  }
}
